import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { NAuthClientError } from '@nauth-toolkit/client';
import { Button, Panel, buttonClassName } from '../../components';
import { GoogleIcon } from './GoogleIcon';
import { passwordPolicyIssues, signInOutcome, startGoogleSignIn } from './api';
import { useRespondToPasswordChallenge, useSignIn } from './useAuth';
import { safeNextPath } from '../../lib/nextPath';

/**
 * Step two, for a password somebody else chose.
 *
 * Rendered in place of the sign-in form rather than at its own route, and that
 * is deliberate on both counts. The challenge handle is single-use and
 * short-lived, so putting it in a URL would make a refresh or a shared link a
 * dead end; and the person is mid-sign-in, not starting something new, so
 * keeping the same card is what makes it read as one continuous act.
 *
 * There is a way back, though not a useful one mid-flow: the challenge handle
 * is single-use and expires in minutes, and without an escape the person who
 * left the tab open over lunch is left on a form that can only fail, with no
 * control that returns them to the sign-in they came from.
 */
function ChoosePassword({
  session,
  onDone,
  onAbandon,
}: {
  session: string;
  onDone: () => void;
  onAbandon: () => void;
}) {
  const respond = useRespondToPasswordChallenge();
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [stuck, setStuck] = useState<string | null>(null);

  const policyIssues = passwordPolicyIssues(newPassword);
  const matches = newPassword !== '' && newPassword === confirmation;
  const canSubmit = policyIssues.length === 0 && matches;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    setStuck(null);
    respond.mutate(
      { session, newPassword },
      {
        onSuccess: (response) => {
          // nauth re-evaluates its challenge list after every step, so this
          // reply can itself be another challenge.
          const outcome = signInOutcome(response);
          if (outcome.kind === 'signed-in') {
            onDone();
            return;
          }
          setStuck(
            outcome.kind === 'must-change-password'
              ? 'That password was not accepted. Try a different one.'
              : `This account needs a step Rundown cannot show yet (${outcome.challengeName}). Email your project lead.`,
          );
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <p className="fs-13 text-ink-soft">
        This password was set for you. Choose one only you know to finish signing in.
      </p>

      <label className="flex flex-col gap-1.5">
        <span className="fs-12 font-medium text-ink-soft">New password</span>
        <input
          type="password"
          autoComplete="new-password"
          required
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          className="h-11 rounded-lg border border-line-stronger bg-base px-3.5 text-sm text-ink placeholder:text-faint transition-colors focus:border-accent focus:ring-2 focus:ring-accent-tint"
          placeholder="••••••••••••"
        />
      </label>

      <label className="flex flex-col gap-1.5">
        <span className="fs-12 font-medium text-ink-soft">Confirm new password</span>
        <input
          type="password"
          autoComplete="new-password"
          required
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
          className="h-11 rounded-lg border border-line-stronger bg-base px-3.5 text-sm text-ink placeholder:text-faint transition-colors focus:border-accent focus:ring-2 focus:ring-accent-tint"
          placeholder="••••••••••••"
        />
      </label>

      {newPassword !== '' && policyIssues.length > 0 && (
        <p className="fs-11 text-muted">Still needs: {policyIssues.join(', ')}.</p>
      )}
      {confirmation !== '' && !matches && <p className="fs-11 text-muted">The two passwords do not match.</p>}

      {/* nauth enforces rules this form cannot mirror — it rejects a password
          containing your own email, for one — so its message is shown as-is
          rather than translated into a guess about which rule was missed. */}
      {(respond.error !== null || stuck !== null) && (
        <p role="alert" className="rounded-md bg-danger-tint px-3 py-2 fs-13 text-danger">
          {stuck ?? respond.error?.message}
        </p>
      )}

      <Button
        type="submit"
        variant="primary"
        disabled={respond.isPending || !canSubmit}
        className="mt-1.5 h-11 w-full rounded-lg fs-14 shadow-sm transition-all hover:shadow-md active:shadow-sm"
      >
        {respond.isPending ? 'Saving…' : 'Set password and continue'}
      </Button>

      {/* The one control that works when the handle has expired, which is the
          only state this form cannot recover from on its own. */}
      <button type="button" onClick={onAbandon} className="fs-11.5 text-muted hover:text-ink hover:underline">
        Start again
      </button>
    </form>
  );
}

export function SignIn() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const signIn = useSignIn();

  const next = safeNextPath(searchParams.get('next'));

  // Local state here is *form input*, not server state — TanStack Query owns the
  // latter and never the former.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  /**
   * Set when nauth answers the sign-in with a challenge instead of a session.
   *
   * Not server state — it is a handle to an interaction that is still in
   * progress — so it lives here rather than in the query cache, and it dies
   * with the screen, which is what we want for a single-use token.
   */
  const [challengeSession, setChallengeSession] = useState<string | null>(null);
  const [unsupported, setUnsupported] = useState<string | null>(null);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setUnsupported(null);
    signIn.mutate(
      { email, password },
      {
        onSuccess: (response) => {
          const outcome = signInOutcome(response);
          switch (outcome.kind) {
            case 'signed-in':
              void navigate(next, { replace: true });
              return;
            case 'must-change-password':
              setChallengeSession(outcome.session);
              return;
            // MFA and email verification would land here. Saying so beats
            // navigating into a guarded route with no credential, which is
            // what this screen used to do with every challenge.
            case 'unsupported-challenge':
              setUnsupported(outcome.challengeName);
          }
        },
      },
    );
  };

  const errorMessage =
    unsupported !== null
      ? `This account needs a step Rundown cannot show yet (${unsupported}). Email your project lead.`
      : signIn.error === null
        ? null
        : !(signIn.error instanceof NAuthClientError) || signIn.error.isNetworkError
          ? 'Could not reach the server. Try again.'
          : signIn.error.statusCode === 401
            ? 'That email and password did not match.'
            : signIn.error.message;

  return (
    <main className="relative flex min-h-[100dvh] items-center justify-center overflow-hidden bg-surface px-4 py-10">
      {/* A soft, out-of-focus glow behind the card — the only departure from the
          rest of the app's flat surfaces, reserved for this one first-touch
          screen. Purely decorative, so it's hidden from the accessibility tree. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-0 h-[26.25rem] w-[45rem] -translate-x-1/2 -translate-y-1/3 rounded-full bg-accent-tint blur-3xl"
      />

      <div className="relative w-full max-w-sm">
        <div className="mb-9 flex flex-col items-center text-center">
          {/* The logo image carries the wordmark visually; a screen reader still
              needs a real heading rather than an image alt text to land on. */}
          <h1 className="sr-only">Sign in to Rundown</h1>
          <img src="/rundown-logo.png" alt="Rundown" className="h-9 w-auto" />
          <p className="mt-3 fs-12 font-medium tracking-wide text-faint uppercase">
            AI Project Management Tool
          </p>
        </div>

        <Panel className="shadow-lg shadow-ink/[0.04]">
          {challengeSession !== null ? (
            <ChoosePassword
              session={challengeSession}
              onDone={() => void navigate(next, { replace: true })}
              onAbandon={() => {
                setChallengeSession(null);
                setPassword('');
              }}
            />
          ) : (
            <>
              {/* Google is the primary path: clients are invited by email and sign in
              with the Google account that received the invitation. Styled to
              Google's own branding guidelines rather than as an accent button. */}
              <button
                type="button"
                onClick={() => void startGoogleSignIn(next)}
                className={buttonClassName({
                  variant: 'secondary',
                  className:
                    'h-11 w-full gap-3 rounded-lg fs-14 shadow-sm transition-all hover:border-line-heavy hover:shadow-md active:shadow-sm',
                })}
              >
                <GoogleIcon />
                Continue with Google
              </button>

              <div className="my-5 flex items-center gap-3">
                <span className="h-px flex-1 bg-line" />
                <span className="fs-11 uppercase tracking-wide text-faint">or</span>
                <span className="h-px flex-1 bg-line" />
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
                <label className="flex flex-col gap-1.5">
                  <span className="fs-12 font-medium text-ink-soft">Email</span>
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    className="h-11 rounded-lg border border-line-stronger bg-base px-3.5 text-sm text-ink placeholder:text-faint transition-colors focus:border-accent focus:ring-2 focus:ring-accent-tint"
                    placeholder="you@company.com"
                  />
                </label>

                <label className="flex flex-col gap-1.5">
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="fs-12 font-medium text-ink-soft">Password</span>
                    <Link to="/forgot-password" className="fs-11.5 text-muted hover:text-ink hover:underline">
                      Forgot password?
                    </Link>
                  </span>
                  <input
                    type="password"
                    name="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="h-11 rounded-lg border border-line-stronger bg-base px-3.5 text-sm text-ink placeholder:text-faint transition-colors focus:border-accent focus:ring-2 focus:ring-accent-tint"
                    placeholder="••••••••"
                  />
                </label>

                {errorMessage !== null && (
                  <p role="alert" className="rounded-md bg-danger-tint px-3 py-2 fs-13 text-danger">
                    {errorMessage}
                  </p>
                )}

                <Button
                  type="submit"
                  variant="primary"
                  disabled={signIn.isPending}
                  className="mt-1.5 h-11 w-full rounded-lg fs-14 shadow-sm transition-all hover:shadow-md active:shadow-sm"
                >
                  {signIn.isPending ? 'Signing in…' : 'Sign in with password'}
                </Button>
              </form>
            </>
          )}
        </Panel>

        <p className="mt-6 text-center fs-12 text-faint">Trouble signing in? Email your project lead.</p>
      </div>
    </main>
  );
}
